import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Card, Row, Col, Modal, Badge, Tab, Nav, Image } from "react-bootstrap";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";
import Swal from "sweetalert2";
import { FiDownload, FiZoomIn, FiArrowLeft, FiTrash2, FiEdit2 } from "react-icons/fi";
import "./ProductView.css";

const ProductView = () => {
  const { id, pointindex } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${BASE_URL}product/findById/${id}`, {
          headers: authHeader(),
        });
        setProduct(response.data);
        if (response.data.productImages?.length > 0) {
          setFiles(response.data.productImages);
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch product data.",
          confirmButtonColor: "#0d6efd"
        });
        navigate("/productindex/1");
      } finally {
        setIsLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  const fetchAndOpenFile = async (filename) => {
    try {
      const response = await axios.get(`${BASE_URL}productImages/findImageByFilename/${filename}`, {
        headers: authHeader(),
        responseType: 'blob',
      });
      const contentType = response.headers['content-type'];
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      setFileType(contentType.startsWith("image") ? "image" : "pdf");
      setFileUrl(url);
      setModalShow(true);
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to open file.",
        confirmButtonColor: "#0d6efd"
      });
    }
  };

  const downloadFile = async (filename) => {
    try {
      const response = await axios.get(`${BASE_URL}productImages/findImageByFilename/${filename}`, {
        headers: authHeader(),
        responseType: 'blob',
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: 'Could not download file.',
        confirmButtonColor: "#0d6efd"
      });
    }
  };

  const handleCloseModal = () => {
    setModalShow(false);
    setFileUrl(null);
  };

  // ... (keep existing helper functions like fetchAndOpenFile, downloadFile, handleCloseModal)

  const renderField = (label, value, md = 4) => (
    <Col md={md} className="mb-3">
      <div className="product-field">
        <label className="field-label">{label}</label>
        <div className="field-value">{value || <span className="text-muted">-</span>}</div>
      </div>
    </Col>
  );

  const renderProductDetails = () => {
    if (!product) return null;

    const { productcategory, producttype } = product;
    const isPumpSpare = productcategory === "Pump Spare";
    const isManufacturing = ["International Manufacturing", "Domestic Manufacturing"].includes(productcategory);
    const isFinished = producttype === "Finished";
    const isRawMaterial = producttype === "Raw Material";
    const isSemiFinished = producttype === "Semi Finished";

    // Common fields for all product types
    const commonFields = [
      renderField("Brand", product.brand?.brandname),
      renderField("Product Category",
        <Badge bg="primary" className="text-uppercase">
          {product.productcategory}
        </Badge>
      ),
      renderField("Stock Group", product.productGroup?.groupname),
      renderField("Product Type",
        <Badge bg={producttype === "Finished" ? "success" :
          producttype === "Raw Material" ? "warning" : "info"}>
          {product.producttype}
        </Badge>
      )
    ];

    // Pump Spare specific fields
    const pumpSpareFields = isPumpSpare ? [
      renderField("Product Description", product.description, 12),
      renderField("Customer Name", product.customer ? `${product.customer.companyname} - ${product.customer.contactperson}` : "-"),
      renderField("Model Number", product.modelnumber),
      renderField("Tag Number", product.tagnumber),
      renderField("Item Code", product.itemcode),
      renderField("HSN Code", product.hsnsacno),
      renderField("Serial Number", product.serialnumber),
      renderField("MOC", product.moc),
      renderField("Part No", product.partno),
      renderField("Unit Of Measurement", product.unitofmeasurement),
      renderField("IGST", `${product.igst}%`),
      renderField("CGST", `${product.cgst}%`),
      renderField("SGST", `${product.sgst}%`)
    ] : [];

    // Manufacturing fields - Finished products
    const manufacturingFinishedFields = (isManufacturing && isFinished) ? [
      renderField("Product Description", product.description, 12),
      renderField("Customer Name", product.customer ? `${product.customer.companyname} - ${product.customer.contactperson}` : "-"),
      renderField("Model Number", product.modelnumber),
      renderField("Tag Number", product.tagnumber),
      renderField("Item Code", product.itemcode),
      renderField("HSN Code", product.hsnsacno),
      renderField("Serial Number", product.serialnumber),
      renderField("MOC", product.moc),
      renderField("Part No", product.partno),
      renderField("Labour Charges %", product.labourcharges),
      renderField("Unit Of Measurement", product.unitofmeasurement),
      renderField("IGST", `${product.igst}%`),
      renderField("CGST", `${product.cgst}%`),
      renderField("SGST", `${product.sgst}%`)
    ] : [];

    // Manufacturing fields - Raw Material
    const manufacturingRawMaterialFields = (isManufacturing && isRawMaterial) ? [
      renderField("Product Description", product.description, 12),
      renderField("MOC", product.moc),
      renderField("Size", product.size),
      renderField("Part No", product.partno),
      renderField("HSN Code", product.hsnsacno),
      renderField("Schedule", product.schedule),
      renderField("Class", product.classes),
      renderField("Unit Of Measurement", product.unitofmeasurement),
      renderField("IGST", `${product.igst}%`),
      renderField("CGST", `${product.cgst}%`),
      renderField("SGST", `${product.sgst}%`)
    ] : [];

    // Manufacturing fields - Semi Finished
    const manufacturingSemiFinishedFields = (isManufacturing && isSemiFinished) ? [
      renderField("Product Description", product.description, 12),
      renderField("Item Code", product.itemcode),
      renderField("MOC", product.moc),
      renderField("Size", product.size),
      renderField("Part No", product.partno),
      renderField("HSN Code", product.hsnsacno),
      renderField("Schedule", product.schedule),
      renderField("Class", product.classes),
      renderField("Rating", product.rating),
      renderField("Type", product.type),
      renderField("Unit Of Measurement", product.unitofmeasurement),
      renderField("IGST", `${product.igst}%`),
      renderField("CGST", `${product.cgst}%`),
      renderField("SGST", `${product.sgst}%`)
    ] : [];

    return [
      ...commonFields,
      ...pumpSpareFields,
      ...manufacturingFinishedFields,
      ...manufacturingRawMaterialFields,
      ...manufacturingSemiFinishedFields
    ];
  };

  const renderImages = () => (
    <div className="gallery-container">
      {files.length ? (
        <Row className="g-3">
          {files.map((file, i) => (
            <Col key={i} xs={6} sm={4} md={3} lg={2}>
              <div className="gallery-item">
                <div className="image-wrapper" onClick={() => fetchAndOpenFile(file.filename)}>
                  <Image
                    src={`${BASE_URL}productImages/findImageByFilename/${file.filename}`}
                    alt={`Product ${i + 1}`}
                    thumbnail
                    className="gallery-image"
                  />
                  <div className="image-overlay">
                    <FiZoomIn size={24} />
                  </div>
                </div>
                <div className="file-actions">
                  <small className="file-name text-truncate">{file.filename}</small>
                  <button
                    className="button-70 btn-outline-primary"
                    onClick={() => downloadFile(file.filename)}
                  >
                    <FiDownload size={14} />
                  </button>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      ) : (
        <div className="no-files text-center py-4">
          <p className="text-muted">No images available for this product</p>
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading product details...</p>
      </div>
    );
  }

  // ... (keep existing getItem function)

  const getItem = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(BASE_URL + "product/delete/" + id, {
          headers: authHeader(),
        })

          .then(response => {
            console.log('product', response);
            if (response.data.length === 0) {
              Swal.fire(
                'Warning',
                'Product present in another module. Delete operation has been cancelled.',
                'warning'
              );
            } else {
              console.log(response.data);
              navigate(`/productindex/${pointindex}`);
              Swal.fire(
                'Deleted!',
                'Deleted successfully.',
                'success'
              );
            }
          })
          .catch((error) => {
            Swal.fire(
              'Error',
              error.response.data,
              'error'
            );
          });
      }
    });
  };


  return (
    <div className="product-view-container">
      <Card className="product-card">
        <Card.Header className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <Badge bg="secondary" className="product-id me-3">
              ID: {id}
            </Badge>
            <h4 className="mb-0 text-teal-800">Product Details</h4>
          </div>

          <div className="d-flex">
            <button
              className="btn btn-danger me-2 d-flex align-items-center"
              onClick={() => getItem(id)}
            >
              <FiTrash2 className="me-1" /> Delete
            </button>
            <Link
              to={`/productEdit/${id}/${pointindex}`}
              className="btn btn-teal d-flex align-items-center me-2"
            >
              <FiEdit2 className="me-1" /> Edit
            </Link>
            <button
              className="btn btn-outline-teal d-flex align-items-center"
              onClick={() => navigate("/productindex/1")}
            >
              <FiArrowLeft className="me-1" /> Back
            </button>
          </div>
        </Card.Header>

        <Card.Body>
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
            <Nav variant="tabs" className="mb-4 custom-tabs">
              <Nav.Item>
                <Nav.Link eventKey="details" className="text-teal-800">
                  Details
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="images" className="text-teal-800">
                  Images ({files.length})
                </Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              <Tab.Pane eventKey="details">
                <Row className="product-details">
                  {renderProductDetails()}
                </Row>
              </Tab.Pane>
              <Tab.Pane eventKey="images">
                {renderImages()}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>

      <Modal show={modalShow} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>File Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {fileUrl && (fileType === "pdf" ? (
            <iframe
              title="PDF Viewer"
              src={fileUrl}
              width="100%"
              height="500px"
              className="pdf-viewer"
            />
          ) : (
            <Image src={fileUrl} alt="Preview" fluid />
          ))}
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-primary"
            onClick={() => downloadFile(fileUrl.split('/').pop())}
          >
            <FiDownload className="me-2" /> Download
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProductView;